import {Box, Link} from '@primer/react'
import {graphql, useFragment, usePreloadedQuery, useQueryLoader, type PreloadedQuery} from 'react-relay'

import {useEffect, type RefObject} from 'react'
import {LABELS} from '../../constants/labels'
import {SafeHTMLText, type SafeHTMLString} from '@github-ui/safe-html'
import {issueHovercardPath} from '@github-ui/paths'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'
import type {HeaderParentTitleQuery} from './__generated__/HeaderParentTitleQuery.graphql'

import {getIcon} from '../sections/relations-section/ParentIssue'
import type {OptionConfig} from '../OptionConfig'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'

type HeaderParentTitleProps = {
  parentKey: HeaderParentTitle$key
} & SharedProps

type SharedProps = {
  optionConfig?: OptionConfig
  containerRef?: RefObject<HTMLElement> | undefined
}

// This query is not actually used directly, but we use it to fetch the data from store,
// which should exist because the same fragment is fetched in the issue viewer secondary query.
export const HeaderParentTitleGraphQLQuery = graphql`
  query HeaderParentTitleQuery($issueId: ID!) {
    node(id: $issueId) {
      ... on Issue {
        ...HeaderParentTitle
      }
    }
  }
`

/** Component for rendering the header parent title, which asynchronously fetches the parent data from the secondary query */
export const HeaderParentTitle = ({issueId, ...props}: {issueId: string} & SharedProps) => {
  const [queryRef, loadQueryRef] = useQueryLoader<HeaderParentTitleQuery>(HeaderParentTitleGraphQLQuery)

  useEffect(() => loadQueryRef({issueId}, {fetchPolicy: 'store-only'}), [loadQueryRef, issueId])
  if (!queryRef) return null

  return <HeaderParentTitleFetched queryRef={queryRef} {...props} />
}

/** Component that is rendered only when the parent title has finally been fetched */
export const HeaderParentTitleFetched = ({
  queryRef,
  ...props
}: {
  queryRef: PreloadedQuery<HeaderParentTitleQuery>
} & SharedProps) => {
  const secondaryData = usePreloadedQuery<HeaderParentTitleQuery>(HeaderParentTitleGraphQLQuery, queryRef)

  if (!secondaryData.node) return null

  return <HeaderParentTitleInternal parentKey={secondaryData.node} {...props} />
}

/** The internal component that renders the parent title itself after all data fetching has occurred */
const HeaderParentTitleInternal = ({parentKey, optionConfig, containerRef}: HeaderParentTitleProps) => {
  const data = useFragment(
    graphql`
      fragment HeaderParentTitle on Issue {
        parent {
          number
          repository {
            name
            owner {
              login
            }
          }
          state
          stateReason
          title
          url
        }
      }
    `,
    parentKey,
  )
  const {parent} = data

  const breakpoint = useContainerBreakpoint(containerRef?.current ?? null)

  if (!parent) {
    return null
  }

  const repo = parent.repository.name
  const owner = parent.repository.owner.login

  const stateIcon = getIcon(parent.state, parent.stateReason, {width: 14, height: 14})
  if (!stateIcon) return null

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        color: 'fg.muted',
        height: 24,
        top: optionConfig?.useViewportQueries
          ? ['-8px', '-8px', '-21px', '-21px']
          : breakpoint(['-8px', '-8px', '-21px', '-21px']),
        position: 'absolute',
        /**
         * NOTE: The queries below are used to determine the width of the parent title. The width is calculated based on
         * the viewport width or the pane width (if in a pinned side panel). These aren't ideal implementations, and
         * we may want to consider porting this to a more robust solution (like container queries) in the future for
         * the padding/margin calculations as well. (https://github.com/github/sub-issues/issues/438)
         */
        // Calculating the *actual* computed width of the panel, which is clamped between the min and max width.  This
        // is not stored as a variable, so we are computing it here.
        '--effective-pane-width': 'clamp(var(--pane-min-width),var(--pane-width),var(--pane-max-width))',
        // If we are inside of the side panel (pinned or not), then --pane-width will be defined and be used. Otherwise,
        // we will fall back to 100% of the parent container width and subtract some pixels to account for padding and such.
        width: 'calc(var(--effective-pane-width, 100%) - 150px)',
        // No matter the viewport size: we always try to show the first 25 characters of the parent issue title. Then, if the
        // viewport size (or pane width if in a pinned side panel in Projects), then we try to take up to 50% of the width.
        minWidth: 'max(var(--effective-pane-width, 100vw)*0.5, 25ch)',
      }}
    >
      {stateIcon}
      <Link
        aria-label={`Parent issue: ${parent.title}`}
        href={parent.url}
        data-hovercard-url={issueHovercardPath({
          owner,
          repo,
          issueNumber: parent.number,
        })}
        // We will truncate the title text to a single line since it can be very long and we don't want to
        // cause any layout shift if it goes to two lines. 85% of the viewport width was chosen as a value that looks
        // reasonably good on narrow viewports, and scales up well on large viewports
        sx={{
          color: 'fg.muted',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          // Need to set font size and line height explicityle since the parent title is not a part of any primer page header sub components.
          fontSize: 'var(--text-body-size-medium)',
          lineHeight: 'var(--text-body-lineHeight-medium)',
        }}
        target="_blank"
      >
        <SafeHTMLText html={parent.title as SafeHTMLString} /> <Box as="span">{LABELS.issueNumber(parent.number)}</Box>
      </Link>
    </Box>
  )
}

try{ HeaderParentTitle.displayName ||= 'HeaderParentTitle' } catch {}
try{ HeaderParentTitleFetched.displayName ||= 'HeaderParentTitleFetched' } catch {}
try{ HeaderParentTitleInternal.displayName ||= 'HeaderParentTitleInternal' } catch {}